import { rows, TableName } from '@/db/database'
import { requireDevice } from '@/lib/device'
import { newTempId } from '@/lib/tempId'
import { syncEngine } from '@/sync/SyncEngine'
import { PosSync } from '@/types'

type Row = PosSync.LocalRow

/**
 * Catalog edits on the device (POS.md §18.7 Q2): written here, pushed by sync, last write wins on the server. Records
 * are never deleted from a till (the sync has no delete); they are switched to `inactive` instead.
 */
export type CatalogTable = Extract<TableName, 'categories' | 'brands' | 'measurement_units' | 'products' | 'product_variations'>

const live = (row: Row) => !row.deleted_at && row.synced !== -1

/** Every row of a catalog table that is not deleted, active or not, for the edit screens. */
export const listRows = async (table: CatalogTable) => (await rows(table).toArray()).filter(live)

/** Points at a parent by server id once it has one, else by its temp id; clears both when there is none. */
const parentRef = async (table: TableName, localId: string | null | undefined, idField: string, tempField: string) => {
  const parent = localId ? ((await rows(table).get(localId)) ?? (await rows(table).where('_id').equals(localId).first())) : undefined
  if (!parent) return { [idField]: null, [tempField]: null }
  return parent._id ? { [idField]: String(parent._id), [tempField]: null } : { [idField]: null, [tempField]: parent.temp_id }
}

/** An edit marks the row to push again; a new row gets a temp id. Returns the row's local id. */
const save = async (table: TableName, id: string | undefined, fields: Record<string, unknown>) => {
  if (id) {
    if (!(await rows(table).get(id))) throw new Error('This record is no longer on the device.')
    await rows(table).update(id, { ...fields, synced: 0, issue_fields: undefined })
  } else {
    const tempId = newTempId(requireDevice().terminal_unique_no)
    await rows(table).add({ status: 'active', ...fields, id: tempId, temp_id: tempId, synced: 0, created_at: new Date().toISOString() })
    id = tempId
  }
  syncEngine.requestSync()
  return id
}

const clash = async (table: TableName, id: string | undefined, test: (row: Row) => boolean) =>
  (await rows(table).toArray()).find((row) => row.id !== id && live(row) && test(row))

const sameText = (a: unknown, b: string) => String(a ?? '').trim().toLowerCase() === b.trim().toLowerCase()

export const saveCategory = (id: string | undefined, input: { name: string; status: string }) =>
  save('categories', id, { name: input.name.trim(), status: input.status })

export const saveBrand = (id: string | undefined, input: { name: string; status: string }) =>
  save('brands', id, { name: input.name.trim(), status: input.status })

/** Unit titles are unique per business on the server; a clash would fail the whole push of units. */
export const saveUnit = async (id: string | undefined, input: { title: string; status: string }) => {
  if (await clash('measurement_units', id, (u) => sameText(u.title, input.title))) throw new Error(`A unit called “${input.title}” exists.`)
  return save('measurement_units', id, { title: input.title.trim(), status: input.status })
}

export interface ProductInput {
  name: string
  category_id?: string | null
  brand_id?: string | null
  measurement_unit_id?: string | null
  sale_vat: number
  price_type: 'fixed' | 'variable'
  sold_by_unit: boolean
  status: string
}

/** Product slugs are unique per business on the server and come from the name; a clash fails the push. */
export const saveProduct = async (id: string | undefined, input: ProductInput) => {
  if (await clash('products', id, (p) => sameText(p.name, input.name))) throw new Error(`A product called “${input.name}” exists.`)
  return save('products', id, {
    name: input.name.trim(),
    ...(await parentRef('categories', input.category_id, 'category_id', 'category_temp_id')),
    ...(await parentRef('brands', input.brand_id, 'brand_id', 'brand_temp_id')),
    ...(await parentRef('measurement_units', input.measurement_unit_id, 'measurement_unit_id', 'measurement_unit_temp_id')),
    sale_vat: input.sale_vat,
    price_type: input.price_type,
    sold_by_unit: input.sold_by_unit,
    status: input.status,
    ...(id ? {} : { delivery_return_policy: '' }),
  })
}

export interface VariationInput {
  product_id: string
  name?: string
  sku: string
  regular_price: number
  min_stock_quantity: number
  barcodes: string[]
  status: string
}

/** `plu_code` is the server's (assigned on the first sync, POS.md §18.8) and never sent from here. */
export const saveVariation = async (id: string | undefined, input: VariationInput) => {
  const barcodes = [...new Set(input.barcodes.map((b) => b.trim()).filter(Boolean))]
  const taken = await clash('product_variations', id, (v) => Array.isArray(v.barcodes) && v.barcodes.some((b) => barcodes.includes(String(b))))
  if (taken) throw new Error(`A barcode is already used by ${taken.name || taken.sku}.`)
  if (await clash('product_variations', id, (v) => sameText(v.sku, input.sku))) throw new Error(`SKU ${input.sku} is already used.`)
  return save('product_variations', id, {
    ...(await parentRef('products', input.product_id, 'product_id', 'product_temp_id')),
    name: input.name?.trim() || null,
    sku: input.sku.trim(),
    regular_price: input.regular_price,
    min_stock_quantity: input.min_stock_quantity,
    barcodes,
    status: input.status,
  })
}

/**
 * This branch's price for a variation, in a price book or (book null) the branch price (§7.1). One row per variation
 * and book on the server; an existing row is edited. A price of 0 falls back to the next price in line.
 */
export const saveBranchPrice = async (variationLocalId: string, priceBookId: string | null, price: number) => {
  const device = requireDevice()
  const variation = await rows('product_variations').get(variationLocalId)
  if (!variation) throw new Error('This product is no longer on the device.')
  const refs = [variation.id, variation._id].filter(Boolean).map(String)
  const existing = (await rows('branch_product_variations').toArray()).find(
    (bp) =>
      String(bp.price_book_id ?? '') === String(priceBookId ?? '') &&
      (refs.includes(String(bp.product_variation_id)) || refs.includes(String(bp.product_variation_temp_id)))
  )
  return save('branch_product_variations', existing?.id, {
    branch_id: device.branch_id,
    ...(await parentRef('product_variations', variationLocalId, 'product_variation_id', 'product_variation_temp_id')),
    price_book_id: priceBookId,
    sale_price: price,
  })
}
